import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Clock, Send, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Supplier · Revised Quotations
 * Lists active negotiations the supplier is shortlisted in with an inline
 * submit form (revised amount + comments) and a Decline option.
 * Historic rounds are visible but read-only.
 */
export default function SupplierNegotiations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/supplier/negotiations`, { withCredentials: true });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load negotiations');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (id) => {
    const d = drafts[id] || {};
    if (!d.amount || Number(d.amount) <= 0) return toast.error('Enter a valid revised amount');
    setBusyId(id);
    try {
      await axios.post(`${API}/supplier/negotiations/${id}/submit-revised`, { revised_amount: Number(d.amount), comments: d.comments || '' }, { withCredentials: true });
      toast.success('Revised quotation submitted');
      setDrafts(s => ({ ...s, [id]: {} }));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
    finally { setBusyId(null); }
  };

  const decline = async (id) => {
    if (!window.confirm('Decline participation in this negotiation round?')) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/supplier/negotiations/${id}/decline`, {}, { withCredentials: true });
      toast.success('Declined');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to decline'); }
    finally { setBusyId(null); }
  };

  if (loading) return <div className="p-6 text-center text-muted-foreground text-sm"><Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" /> Loading…</div>;

  return (
    <div className="space-y-4" data-testid="supplier-negotiations">
      <h2 className="text-lg font-bold text-navy">Negotiation Invitations</h2>
      {items.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm" data-testid="no-negotiations">You have no active negotiation invitations.</div>}
      {items.map(n => {
        const canSubmit = n.current_round_status === 'active' && n.is_shortlisted_in_current_round;
        const submitted = n.my_latest_submission?.status === 'submitted' && n.my_latest_submission?.round_number === n.current_round;
        return (
          <div key={n.id} className="bg-white border border-border rounded-lg p-4" data-testid={`sup-neg-${n.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-semibold text-navy">{n.parent_title || (n.parent_type === 'tender' ? 'Tender' : 'Quick Quotation')} <span className="text-xs text-muted-foreground">· {n.parent_type}</span></div>
                <div className="text-xs text-muted-foreground">Round {n.current_round}{n.current_round_status && <> · <span className="uppercase">{n.current_round_status}</span></>}</div>
              </div>
              {n.deadline_at && <div className="text-xs text-orange flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(n.deadline_at).toLocaleString()}</div>}
            </div>
            {n.my_latest_submission && (
              <div className="text-xs mb-3">
                Latest submission (round {n.my_latest_submission.round_number}): {' '}
                {n.my_latest_submission.status === 'submitted' && <span className="text-green-700 font-semibold">₹{Number(n.my_latest_submission.revised_amount).toLocaleString('en-IN')}</span>}
                {n.my_latest_submission.status === 'declined' && <span className="text-orange">Declined</span>}
                {n.my_latest_submission.status === 'not_responded' && <span className="text-red-700">Not responded</span>}
              </div>
            )}
            {canSubmit && !submitted && (
              <div className="border-t border-border pt-3 grid sm:grid-cols-[1fr_2fr_auto_auto] gap-2">
                <input type="number" min="1" step="0.01" placeholder="Revised amount (₹)" value={(drafts[n.id]?.amount) || ''} onChange={e => setDrafts(s => ({ ...s, [n.id]: { ...s[n.id], amount: e.target.value } }))} className="px-3 py-2 rounded border border-input text-sm" data-testid={`amount-${n.id}`} />
                <input type="text" placeholder="Comments (optional)" value={(drafts[n.id]?.comments) || ''} onChange={e => setDrafts(s => ({ ...s, [n.id]: { ...s[n.id], comments: e.target.value } }))} className="px-3 py-2 rounded border border-input text-sm" data-testid={`comments-${n.id}`} />
                <button onClick={() => submit(n.id)} disabled={busyId === n.id} className="px-3 py-1.5 rounded bg-navy text-white text-xs font-bold inline-flex items-center disabled:opacity-50" data-testid={`submit-${n.id}`}>
                  <Send className="h-3.5 w-3.5 mr-1" /> {submitted ? 'Update' : 'Submit'}
                </button>
                <button onClick={() => decline(n.id)} disabled={busyId === n.id} className="px-3 py-1.5 rounded border border-orange text-orange text-xs font-bold inline-flex items-center disabled:opacity-50" data-testid={`decline-${n.id}`}>
                  <XCircle className="h-3.5 w-3.5 mr-1" /> Decline
                </button>
              </div>
            )}
            {submitted && <div className="text-xs text-green-700 font-medium">✔ Your revised quotation has been recorded for this round.</div>}
            {n.current_round_status === 'closed' && <div className="text-xs text-muted-foreground">Round is closed. Awaiting buyer action.</div>}
          </div>
        );
      })}
    </div>
  );
}
